import React, { useEffect, useState } from "react";
import {
  deleteTemplate,
  duplicateTemplateById,
  listAllTemplates,
} from "../api/admin/templates";
import { TemplateGridItem } from "../components/templates/templates-list-item";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import Loader from "../components/common/loader/loader";
import Error from "../components/common/error/error";
import { cn, notify } from "../lib/utils";
import { siteSettings } from "../settings/site-settings";
import TableHeaders from "../components/ui/table-headers";
import { TableColumn } from "../interfaces/tables";
import { MdContentCopy, MdDelete, MdEdit } from "react-icons/md";
import Table from "../components/ui/table";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { GridIcon, TableIcon } from "@radix-ui/react-icons";
import { PlusIcon } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import GridColumnsSelect from "../components/ui/grid-layout/grid-columns-select";
import { Cog } from "lucide-react";
import DeleteTemplateDialog from "../components/templates/delete-template-dialog";
import usePageTitle from "../hooks/usePageTitle";
import { GoCheckCircleFill, GoXCircleFill } from "react-icons/go";

function Templates() {
  usePageTitle("Templates");

  const navigate = useNavigate();

  // fetch templates from server
  const [templates, setTemplates] = useState([]);
  const [tableData, setTableData] = React.useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // table headers state, search, sort, page.
  const [sort, setSort] = React.useState<any>({ createdAt: -1 });
  const [search, setSearch] = React.useState(null);

  const [page, setPage] = React.useState(1);

  const [pageSize, setPageSize] = React.useState(
    siteSettings.tables.templates.defaultPageSize.key
  );

  const [filters, setFilters] = React.useState<any>({}); // pre-made filters published/unpublished

  const [paginationTotalCount, setPaginationTotalCount] = React.useState(0);

  const [layoutSettings, setLayoutSettings] = useState({
    type: "grid", // grid or table
    gridColumns: 4,
  });

  const [selectedTemplateForDelete, setSelectedTemplateForDelete] =
    React.useState<any | null>(null);

  const [templateDeleteDialogOpen, setTemplateDeleteDialogOpen] =
    React.useState(false);

  const handleOpenDeleteTemplateDialog = (template: any) => {
    setSelectedTemplateForDelete(template);
    setTemplateDeleteDialogOpen(true);
  };

  const handleCloseDeleteTemplateDialog = () => {
    setSelectedTemplateForDelete(null);
    setTemplateDeleteDialogOpen(false);
  };

  const saveLayoutSettingsToLocalStorage = (layoutSettings: any) => {
    localStorage.setItem(
      "templatesLayoutSettings",
      JSON.stringify(layoutSettings)
    );
  };

  const getLayoutSettingsFromLocalStorage = () => {
    const layoutSettings = localStorage.getItem("templatesLayoutSettings");
    return layoutSettings ? JSON.parse(layoutSettings) : null;
  };

  useEffect(() => {
    const layoutSettings = getLayoutSettingsFromLocalStorage();
    if (layoutSettings) {
      setLayoutSettings(layoutSettings);
    }
  }, []);

  // sync

  useEffect(() => {
    saveLayoutSettingsToLocalStorage(layoutSettings);
  }, [layoutSettings]);

  const getGridCss = (columns: number) => {
    if (columns === 6)
      return "grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 ";

    if (columns === 5)
      return "grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";

    if (columns === 4)
      return "grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

    if (columns === 3) return "grid sm:grid-cols-2 md:grid-cols-3";

    if (columns === 2) return "grid sm:grid-cols-2";

    return "grid sm:grid-cols-1";
  };

  const handleDuplicateTemplate = async (templateId: string) => {
    // set loading to true
    setLoading(true);

    // Give browser control
    await new Promise((resolve) => setTimeout(resolve, 0));

    const response = await duplicateTemplateById(templateId);

    // post request return 201 if successful
    if (!response || response.status !== 201) {
      notify({ type: "error", message: "Error duplicating template" });
      setLoading(false);
      return;
    }

    notify({
      type: "success",
      message: "Template duplicated successfully",
    });

    loadTemplates();
  };

  const loadTemplates = async () => {
    const response = await listAllTemplates({
      page,
      sort,
      search,
      pageSize,
      filters,
    });

    if (!response || response.status !== 200) {
      setError("Error loading templates");
      setLoading(false);
      return;
    }

    setTemplates(response.data?.data);
    setTableData(formatDataForTable(response.data?.data));
    setPaginationTotalCount(response.data?.total);
    setLoading(false);
    setError(null);
  };

  const handleOnSearchChange = (searchObj: any) => {
    if (!searchObj || !searchObj.value || searchObj.value === "") {
      setSearch(null);
      return;
    }

    setSearch(searchObj);
    setPage(1); // reset page to 1 when search changes
  };

  const handleOnSortChange = (sortObj: any) => {
    if (!sortObj) {
      setSort(null);
      return;
    }

    setSort(sortObj);
  };

  const handlePublishedFilterChange = (key: string) => {
    if (key === "all") {
      setFilters({});
    } else {
      setFilters({ isPublished: key === "published" ? true : false });
    }

    setPage(1); // reset page to 1 when filter changes
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  useEffect(() => {
    loadTemplates();
  }, [page, sort, search, pageSize, filters]);

  const handleOnDeleteTemplate = async (templateId: string) => {
    const response = await deleteTemplate(templateId);

    if (!response || response.status !== 200) {
      notify({ type: "error", message: "Error deleting template" });
      return;
    }

    notify({
      type: "success",
      message: "Template deleted successfully",
    });

    handleCloseDeleteTemplateDialog();

    loadTemplates();
  };

  const columns: TableColumn[] = [
    {
      key: "previewImage",
      label: "Preview Image",
      render: (cell, row) => {
        if (cell) {
          return (
            <div>
              <div className="group flex w-min hover:cursor-zoom-in relative">
                <img
                  src={cell}
                  alt="category"
                  className="h-[50px] max-w-[50px] object-cover rounded-md"
                />

                <div className="opacity-0 group-hover:opacity-100 fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 bg-borderColor border border-white rounded w-[500px] h-[500px] transition-opacity duration-0 ease-in group-hover:duration-500 group-hover:ease-out pointer-events-none">
                  <img
                    src={cell}
                    alt="category"
                    className="w-full h-full object-contain rounded-md"
                  />
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="w-[35px] h-[50px] bg-borderColor rounded-md flex items-center justify-center text-gray-500">
              N/A
            </div>
          );
        }
      },
    },

    { key: "_id", label: "Internal Id" },

    { key: "internalName", label: "Internal Name" },

    {
      key: "isPublished",
      label: "Published",

      render: (cell, row) => {
        return (
          <div className="flex items-center">
            {cell ? (
              <GoCheckCircleFill size={20} className="text-green-500" />
            ) : (
              <GoXCircleFill size={20} className="text-red-500" />
            )}
          </div>
        );
      },
    },

    { key: "slug", label: "Slug" },

    {
      key: "projects",
      label: "Project(s) Belongs To",
      render: (cell, row) => (
        <div className="w-full flex items-center /justify-between ">
          <span className="font-bold">({cell?.length || 0})</span>

          {cell &&
            cell.length > 0 &&
            cell.slice(0, 2).map((project: any, index: number) => (
              <div
                key={index}
                className={cn("text-sm w-max h-full ", {
                  "ml-1": index < 2,
                })}
              >
                {project.name}
                {index < 1 && cell.length > 1 && ","}
              </div>
            ))}

          {cell && cell.length > 2 && (
            <div className="ml-1 flex">
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <PlusIcon size={16} className="text-primary" />
                  </Tooltip.Trigger>

                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] bg-black text-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]"
                      sideOffset={5}
                    >
                      <div className="flex flex-col gap-y-2">
                        {cell.map((project: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-x-2"
                          >
                            - <span>{project.name}</span>
                          </div>
                        ))}
                      </div>
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "categories",
      label: "Categories",
      render: (cell, row) => (
        <div className="w-full flex items-center /justify-between ">
          <span className="font-bold">({cell?.length || 0})</span>

          {cell &&
            cell.length > 0 &&
            cell.slice(0, 2).map((category: any, index: number) => (
              <div
                key={index}
                className={cn("text-sm w-max h-full ", {
                  "ml-1": index < 2,
                })}
              >
                {category.internalName}
                {index < 1 && cell.length > 1 && ","}
              </div>
            ))}

          {cell && cell.length > 2 && (
            <div className="ml-1 flex">
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <PlusIcon size={16} className="text-primary" />
                  </Tooltip.Trigger>

                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] bg-black text-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]"
                      sideOffset={5}
                    >
                      <div className="flex flex-col gap-y-2">
                        {cell.map((cat: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-x-2"
                          >
                            - <span>{cat.internalName}</span>
                          </div>
                        ))}
                      </div>
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "subcategories",
      label: "Subcategories",
      render: (cell, row) => (
        <div className="w-full flex items-center /justify-between ">
          <span className="font-bold">({cell?.length || 0})</span>

          {cell &&
            cell.length > 0 &&
            cell.slice(0, 2).map((subcategory: any, index: number) => (
              <div
                key={index}
                className={cn("text-sm w-max h-full ", {
                  "ml-1": index < 2,
                })}
              >
                {subcategory.internalName}
                {index < 1 && cell.length > 1 && ","}
              </div>
            ))}

          {cell && cell.length > 2 && (
            <div className="ml-1 flex">
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <PlusIcon size={16} className="text-primary" />
                  </Tooltip.Trigger>

                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] bg-black text-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]"
                      sideOffset={5}
                    >
                      <div className="flex flex-col gap-y-2">
                        {cell.map((sub: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-x-2"
                          >
                            - <span>{sub.internalName}</span>
                          </div>
                        ))}
                      </div>
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "tags",
      label: "Tags",
      render: (cell, row) => (
        <div className="w-full flex items-center /justify-between ">
          <span className="font-bold">({cell?.length || 0})</span>

          {cell &&
            cell.length > 0 &&
            cell.slice(0, 2).map((tag: any, index: number) => (
              <div
                key={index}
                className={cn("text-sm w-max h-full ", {
                  "ml-1": index < 2,
                })}
              >
                {tag}
                {index < 1 && cell.length > 1 && ","}
              </div>
            ))}
          {cell && cell.length > 2 && (
            <div className="ml-1 flex">
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <PlusIcon size={16} className="text-primary" />
                  </Tooltip.Trigger>

                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] bg-black text-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]"
                      sideOffset={5}
                    >
                      <div className="flex flex-col gap-y-2">
                        {cell.map((sub: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-x-2"
                          >
                            - <span>{sub}</span>
                          </div>
                        ))}
                      </div>
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          )}
        </div>
      ),
    },

    {
      key: "createdAt",
      label: "Created At",
      render: (cell) => {
        return cell ? new Date(cell).toLocaleDateString() : "N/A";
      },
    },

    {
      key: "btn",
      label: "Actions",
      fixed: true,
      render: (cell, row) => {
        return (
          <div className="flex gap-x-4">
            <Link to={`${ROUTES.DESIGN}/${row._id}`}>
              <MdEdit
                title="Full Edit"
                className="hover:text-primary cursor-pointer"
                size={24}
              />
            </Link>

            <Link
              to={`${ROUTES.TEMPLATE_SETTINGS_FORM}/${row._id}`}
              title="Edit Template's Settings"
            >
              <Cog className="hover:text-primary cursor-pointer" size={24} />
            </Link>

            <MdContentCopy
              title="Clone"
              className="hover:text-primary cursor-pointer"
              size={22}
              onClick={() => {
                handleDuplicateTemplate(row._id);
              }}
            />

            <MdDelete
              title="Delete"
              className="hover:text-red-600 cursor-pointer"
              size={24}
              onClick={() => {
                handleOpenDeleteTemplateDialog(row.fullDoc);
              }}
            />
          </div>
        );
      },
    },
  ];

  const formatDataForTable = (data: any) => {
    return data.map((d: any) => {
      return {
        _id: d._id,
        internalName: d.internalName,
        slug: d.slug,
        projects: d.projects,
        categories: d.categories,
        subcategories: d.subcategories,
        isPublished: d.isPublished,
        tags: d.tags,
        createdAt: d.createdAt,
        previewImage: d.previewImage,
        btn: d._id,
        fullDoc: d,
      };
    });
  };

  const toggleGroupItemClasses =
    "flex size-[40px] items-center justify-center bg-borderColor text-white first:rounded-l-md last:rounded-r-md hover:bg-primary focus:z-10  data-[state=on]:bg-primary";

  return (
    <div className="w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto ">
            <h1 className="text-2xl font-bold text-primary">Templates</h1>
            {paginationTotalCount && paginationTotalCount > 0 && (
              <p className="text-bodyDark">Total: {paginationTotalCount}</p>
            )}
          </div>

          <div className="w-full mx-auto flex gap-x-3 items-end justify-between my-2">
            <div className="flex">
              <ToggleGroup.Root
                className="inline-flex space-x-px rounded-md bg-borderColor"
                type="single"
                value={layoutSettings.type}
                onValueChange={(value) => {
                  if (!value) return; // remove off effect from toggle group

                  setLayoutSettings((prev) => ({
                    ...prev,
                    type: value,
                  }));
                }}
                aria-label="Layout"
              >
                <ToggleGroup.Item
                  className={toggleGroupItemClasses}
                  value="grid"
                  title="Grid layout"
                  aria-label="Grid layout"
                >
                  <GridIcon fontSize={50} />
                </ToggleGroup.Item>

                <ToggleGroup.Item
                  className={toggleGroupItemClasses}
                  value="table"
                  title="Table layout"
                  aria-label="Table layout"
                >
                  <TableIcon />
                </ToggleGroup.Item>
              </ToggleGroup.Root>

              {layoutSettings.type === "grid" && (
                <div className="px-3">
                  <GridColumnsSelect
                    defaultGridColumns={{
                      key: layoutSettings.gridColumns,
                      label: layoutSettings.gridColumns,
                    }}
                    gridColumnsOptions={
                      siteSettings.tables.templates.gridColumnsOptions
                    }
                    onGridColumnsChange={(columns: number) => {
                      setLayoutSettings((prev) => ({
                        ...prev,
                        gridColumns: columns,
                      }));
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <Button
                onClick={() => navigate(ROUTES.DESIGN)}
                className="ml-auto"
              >
                New Template
              </Button>
            </div>
          </div>

          <div className="w-full block  mx-auto my-10 pb-20">
            <TableHeaders
              searchByOptions={siteSettings.tables.templates.searchByOptions}
              onSearchChange={handleOnSearchChange}
              sortByOptions={siteSettings.tables.templates.sortByOptions}
              onSortChange={handleOnSortChange}
              page={page}
              onPageChange={setPage}
              lastPage={
                paginationTotalCount === 0
                  ? 1
                  : Math.ceil(paginationTotalCount / pageSize)
              }
              pageSizeOptions={siteSettings.tables.templates.pageSizeOptions}
              onPageSizeChange={handleOnPageSizeChange}
              defaultPageSize={siteSettings.tables.templates.defaultPageSize}
              showPublishedFilter={true}
              onPublishedFilterChange={handlePublishedFilterChange}
              currentPublishedFilterValue={
                filters.isPublished === true
                  ? "published"
                  : filters.isPublished === false
                  ? "unpublished"
                  : "all"
              }
            />
            {(layoutSettings.type === "table" && (
              <Table columns={columns} data={tableData} />
            )) || (
              <div className="flex w-full /bg-blue-200 h-full">
                <div
                  className={cn(
                    "gap-y-20 mb-20 mt-10 w-full h-full mx-auto place-items-center",
                    getGridCss(layoutSettings.gridColumns)
                  )}
                >
                  {templates.map((template: any) => (
                    <TemplateGridItem
                      key={template._id}
                      template={template}
                      onCloneClick={() => {
                        handleDuplicateTemplate(template._id);
                      }}
                      onDeleteClick={() => {
                        handleOpenDeleteTemplateDialog(template);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <DeleteTemplateDialog
              template={selectedTemplateForDelete}
              open={templateDeleteDialogOpen}
              onClose={handleCloseDeleteTemplateDialog}
              onTemplateDelete={(templateId: string) => {
                handleOnDeleteTemplate(templateId);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Templates;
